/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Checkbox } from '@atlaskit/checkbox';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	type DeploymentEnvironmentType,
	DeploymentEnvironmentTypeOptions,
} from '../../../../common/types';
import type { EnvironmentTypeViewConfiguration } from '../../../../services/my-preference/types';
import messages from './messages';

type Props = {
	environmentTypeViewConfig: EnvironmentTypeViewConfiguration;
	onChange: (config: EnvironmentTypeViewConfiguration) => void;
};

const { PRODUCTION, STAGING, TESTING, DEVELOPMENT, UNMAPPED } = DeploymentEnvironmentTypeOptions;

const isOnlyOneLeft = (environmentTypeViewConfig: EnvironmentTypeViewConfiguration): boolean => {
	let tickCount = 0;
	Object.keys(environmentTypeViewConfig).forEach((environmentType: string) => {
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'EnvironmentTypeViewConfiguration'.
		if (environmentTypeViewConfig[environmentType]) {
			tickCount += 1;
		}
	});

	return tickCount === 1;
};

// TODO: Kill this function & change `EnvironmentTypeViewConfiguration` to use
// the `DeploymentEnvironmentTypeOptions` values as keys directly.
const getPropKeyFromEnvType = (
	envType: DeploymentEnvironmentType,
): 'production' | 'staging' | 'testing' | 'development' | 'unmapped' => {
	switch (envType) {
		case DeploymentEnvironmentTypeOptions.PRODUCTION:
			return 'production';
		case DeploymentEnvironmentTypeOptions.STAGING:
			return 'staging';
		case DeploymentEnvironmentTypeOptions.TESTING:
			return 'testing';
		case DeploymentEnvironmentTypeOptions.DEVELOPMENT:
			return 'development';
		case DeploymentEnvironmentTypeOptions.UNMAPPED:
			return 'unmapped';
		default:
			throw new Error('Unexpected environment type is given');
	}
};

export const EnvironmentTypeConfiguration = ({ onChange, environmentTypeViewConfig }: Props) => {
	const { formatMessage } = useIntl();

	const onCheckboxChange = (
		newValue: boolean,
		environmentType: DeploymentEnvironmentType,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		if (newValue === false && isOnlyOneLeft(environmentTypeViewConfig)) {
			return;
		}

		fireUIAnalytics(analyticsEvent, 'toggleDeploymentEnvironment', {
			environment: environmentType,
			enabled: newValue,
		});

		const newConfig = { ...environmentTypeViewConfig };
		newConfig[getPropKeyFromEnvType(environmentType)] = newValue;
		onChange(newConfig);
	};

	return (
		<>
			<Heading
				testId="software-deployments.ui.sidebar-content.configuration.environment-type.title"
				size="xsmall"
			>
				<span css={titleStyles}>{formatMessage(messages.title)}</span>
			</Heading>
			<Checkbox
				isChecked={environmentTypeViewConfig.production}
				label={formatMessage(messages.production)}
				onChange={(event, analyticsEvent: UIAnalyticsEvent) => {
					onCheckboxChange(event.target.checked, PRODUCTION, analyticsEvent);
				}}
			/>
			<Checkbox
				isChecked={environmentTypeViewConfig.staging}
				label={formatMessage(messages.staging)}
				onChange={(event, analyticsEvent: UIAnalyticsEvent) => {
					onCheckboxChange(event.target.checked, STAGING, analyticsEvent);
				}}
			/>
			<Checkbox
				isChecked={environmentTypeViewConfig.testing}
				label={formatMessage(messages.testing)}
				onChange={(event, analyticsEvent: UIAnalyticsEvent) => {
					onCheckboxChange(event.target.checked, TESTING, analyticsEvent);
				}}
			/>
			<Checkbox
				isChecked={environmentTypeViewConfig.development}
				label={formatMessage(messages.development)}
				onChange={(event, analyticsEvent: UIAnalyticsEvent) => {
					onCheckboxChange(event.target.checked, DEVELOPMENT, analyticsEvent);
				}}
			/>
			<Checkbox
				isChecked={environmentTypeViewConfig.unmapped}
				label={formatMessage(messages.unmapped)}
				onChange={(event, analyticsEvent: UIAnalyticsEvent) => {
					onCheckboxChange(event.target.checked, UNMAPPED, analyticsEvent);
				}}
			/>
		</>
	);
};

const titleStyles = css({
	height: '24px',
	marginTop: 0,
	marginBottom: token('space.100', '8px'),
});

export default EnvironmentTypeConfiguration;
